/**
 * Maps a parsed CSV row (object with various header names) to player-measurements document shape.
 * Handles column name variations from Scouting and General report export CSVs.
 * Stores rawContent for backend records (not displayed on profile).
 */

import { formatFeetInches } from "./feetInches";

function getCol(row, ...names) {
  for (const name of names) {
    const val = row[name];
    if (val !== undefined && val !== null && String(val).trim() !== "") return String(val).trim();
  }
  return "";
}

/** Normalize age to one decimal place (e.g. 20 → "20.0", 20.5 → "20.5"). */
function normalizeAge(val) {
  if (val == null || String(val).trim() === "") return "";
  const s = cleanValue(val);
  if (!s) return "";
  const num = parseFloat(s);
  if (isNaN(num) || num < 0) return s;
  return Number.isInteger(num) ? num.toFixed(1) : String(num);
}

/** Normalize date to MM/DD/YY for storage. */
function normalizeDate(val) {
  if (!val || String(val).trim() === "") return "";
  const s = String(val).trim();
  const sep = s.includes("/") ? "/" : "-";
  const parts = s.split(sep);
  if (parts.length !== 3) return s;
  const mm = parts[0].padStart(2, "0");
  const dd = parts[1].padStart(2, "0");
  const yy = parts[2].length === 2 ? parts[2] : parts[2].slice(-2);
  return `${mm}/${dd}/${yy}`;
}

/** Strip CSV/quote noise; keep height format 6'5.25", hand 8.0"" -> 8.0. */
function cleanValue(val) {
  if (val == null || String(val).trim() === "") return "";
  let s = String(val).trim().replace(/^"+|"+$/g, "").trim();
  if (s.endsWith('""')) s = s.slice(0, -2).trim();
  return s;
}

/** Normalize pure inch values (hands, verticals): snap to quarter-inch and add " symbol, e.g. 8.0 -> 8", 8.25 -> 8.25". */
function normalizeQuarterInchesLabel(val) {
  if (val == null) return "";
  let s = cleanValue(val);
  if (!s) return "";
  // Strip any trailing inch symbol before parsing
  s = s.replace(/"/g, "").trim();
  const num = parseFloat(s);
  if (isNaN(num)) return s;
  // Snap to nearest quarter inch
  const snapped = Math.round(num * 4) / 4;
  const whole = Math.floor(snapped);
  const frac = snapped - whole;
  if (frac === 0) return `${whole}"`;
  if (frac === 0.25) return `${whole}.25"`;
  if (frac === 0.5) return `${whole}.5"`;
  return `${whole}.75"`;
}

/** Normalize feet+inches values (heights, wingspan, standing reach) to our dropdown format. */
function normalizeFeetAndInchesLabel(val) {
  if (val == null) return "";
  let s = cleanValue(val);
  if (!s) return "";
  // Normalize curly quotes to straight
  s = s.replace(/[’‘]/g, "'").replace(/[”“]/g, '"').trim();
  // Extract feet and inches (e.g. 6'7.75 or 6'7)
  const match = s.match(/(\d+)\s*'\s*([\d.]+)/);
  if (!match) {
    return formatFeetInches(s);
  }
  const feet = parseInt(match[1], 10);
  const inchesNum = parseFloat(match[2]);
  if (isNaN(feet) || isNaN(inchesNum)) return formatFeetInches(s);
  const snapped = Math.round(inchesNum * 4) / 4;
  const inchWhole = Math.floor(snapped);
  const frac = snapped - inchWhole;
  let inchLabel;
  if (frac === 0) inchLabel = `${inchWhole}"`;
  else if (frac === 0.25) inchLabel = `${inchWhole}.25"`;
  else if (frac === 0.5) inchLabel = `${inchWhole}.5"`;
  else inchLabel = `${inchWhole}.75"`;
  return formatFeetInches(`${feet}'${inchLabel}`);
}

/**
 * Map one CSV row to a player-measurements payload.
 * @param {Object} row - Parsed CSV row (keys = column headers)
 * @returns {{ playerProfile, playerName, date, event, age, heightShoes, heightNone, heightNotSpecified, weight, wingspan, standingReach, vertMax, vertNoStep, bodyFat, handL, handW, laneAgility, shuttle, sprint, rawContent }}
 */
export function mapCsvRowToMeasurement(row) {
  const playerProfile = getCol(row, "Player ID", "Player Profile ID", "playerProfileID");
  const playerName = getCol(row, "Player Name", "Player Name", "playerName");
  const event = getCol(row, "Event", "event");
  const eventDate = getCol(row, "Event Date", "eventDate");
  const date = normalizeDate(eventDate);
  const age = normalizeAge(getCol(row, "Age", "age"));
  const bodyFat = getCol(row, "Body Fat %", "bodyFat", "Body Fat %");
  const handL = normalizeQuarterInchesLabel(getCol(row, "Hand L", "handL", "Hand Length"));
  const handW = normalizeQuarterInchesLabel(getCol(row, "Hand W", "handW", "Hand Width"));
  const heightNone = normalizeFeetAndInchesLabel(getCol(row, "Height w/o shoes", "heightNone", "Height w/o shoes"));
  const heightShoes = normalizeFeetAndInchesLabel(getCol(row, "Height w/ shoes", "heightShoes", "Height w/ shoes"));
  const heightNotSpecified = normalizeFeetAndInchesLabel(
    getCol(
      row,
      "w/ or w/o shoes not specified*",
      "Height not specified*",
      "*Height not specifed",
      "heightNotSpecified"
    )
  );
  const laneAgility = getCol(row, "Lane Agility", "laneAgility");
  const shuttle = getCol(row, "Shuttle", "shuttle");
  const sprint = getCol(row, "Sprint", "sprint");
  const standingReach = normalizeFeetAndInchesLabel(getCol(row, "Standing Reach", "standingReach"));
  const vertMax = normalizeQuarterInchesLabel(getCol(row, "Vert Max", "vertMax"));
  const vertNoStep = normalizeQuarterInchesLabel(getCol(row, "Vert No Step", "vertNoStep"));
  const weight = getCol(row, "Weight", "weight");
  const wingspan = normalizeFeetAndInchesLabel(getCol(row, "Wingspan", "wingspan"));
  const rawContent = getCol(row, "Raw data", "Raw data", "rawData", "Raw data");

  return {
    playerProfile,
    playerName,
    date,
    event,
    age,
    heightShoes,
    heightNone,
    heightNotSpecified,
    weight,
    wingspan,
    standingReach,
    vertMax,
    vertNoStep,
    bodyFat,
    handL,
    handW,
    laneAgility,
    shuttle,
    sprint,
    rawContent,
  };
}
